import React, { useState, useEffect } from 'react';
import { callAI } from '../services/aiService';
import { storageService, ResearchResult } from '../services/storageService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';

interface AnalysisData {
  commonPatterns: string[];
  toneAnalysis: string;
  topicBreakdown: Record<string, number>;
  engagementTriggers: string[];
}

const STORAGE_KEY_TWEETS = 'researcher_tweets_input';
const STORAGE_KEY_NAME = 'researcher_account_name';
const STORAGE_KEY_RESULT = 'researcher_result';

export const AccountResearcher: React.FC = () => {
  const { t, locale } = useT();
  const [tweetsInput, setTweetsInput] = useState(() => localStorage.getItem(STORAGE_KEY_TWEETS) || '');
  const [accountName, setAccountName] = useState(() => localStorage.getItem(STORAGE_KEY_NAME) || '');
  const [status, setStatus] = useState<'idle' | 'analyzing' | 'complete' | 'error'>('idle');
  const [result, setResult] = useState<AnalysisData | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_RESULT);
    return saved ? JSON.parse(saved) : null;
  });
  const [error, setError] = useState<string | null>(null);
  const [savedResearch, setSavedResearch] = useState<ResearchResult[]>(storageService.getResearchResults());

  useEffect(() => { localStorage.setItem(STORAGE_KEY_TWEETS, tweetsInput); }, [tweetsInput]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_NAME, accountName); }, [accountName]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_RESULT, JSON.stringify(result)); }, [result]);

  const handleAnalyze = async () => {
    if (!tweetsInput.trim()) return;
    setStatus('analyzing');
    setError(null);
    try {
      const tweets = tweetsInput.split('\n').filter(t => t.trim().length > 0);
      const prompt = `Analyze the following posts and extract success patterns:

【Posts】
${tweets.map((t, i) => `${i + 1}. ${t}`).join('\n')}

【Output format (JSON)】
{
  "commonPatterns": ["pattern 1", "pattern 2", "pattern 3", "pattern 4", "pattern 5"],
  "toneAnalysis": "Tone analysis result (2-3 sentences)",
  "topicBreakdown": { "Topic 1": percentage, "Topic 2": percentage },
  "engagementTriggers": ["trigger 1", "trigger 2", "trigger 3"]
}`;
      const response = await callAI(
        [{ role: 'system', content: 'You are a Twitter analytics expert. Identify success patterns in posts accurately.' }, { role: 'user', content: prompt }],
        { temperature: 0.7, maxTokens: 1500, jsonMode: true }
      );
      const parsed: AnalysisData = JSON.parse(response);
      setResult(parsed);
      setStatus('complete');

      const researchItem: ResearchResult = {
        id: Date.now().toString() + Math.random().toString(36).slice(2),
        accountName: accountName || undefined,
        analyzedCount: tweets.length,
        commonPatterns: parsed.commonPatterns,
        toneAnalysis: parsed.toneAnalysis,
        topicBreakdown: parsed.topicBreakdown,
        engagementTriggers: parsed.engagementTriggers,
        timestamp: new Date().toISOString(),
      };
      storageService.addResearchResult(researchItem);
      setSavedResearch(storageService.getResearchResults());
      storageService.addHistoryItem({ type: 'research', content: `Analysis: ${parsed.commonPatterns.slice(0, 3).join(', ')}`, metadata: { inputText: tweetsInput.slice(0, 200), analyzedCount: tweets.length } });
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Analysis failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto">
      <div className="mb-6 lg:mb-[30px]">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 tracking-tight text-text-primary">{t('accountResearcher.title')}</h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">{t('accountResearcher.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left: Input */}
        <div>
          <h2 className="text-lg lg:text-xl mb-4 text-text-primary">{t('accountResearcher.postsToAnalyze')}</h2>
          <input
            type="text"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            placeholder={t('accountResearcher.accountName')}
            className="w-full px-4 py-3 mb-3 bg-surface-2 border border-border rounded-xl text-text-primary text-sm font-[inherit] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />
          <textarea
            value={tweetsInput}
            onChange={(e) => setTweetsInput(e.target.value)}
            placeholder={t('accountResearcher.placeholder')}
            className="w-full min-h-[300px] p-4 lg:p-5 bg-surface-2 border border-border rounded-xl text-text-primary text-base resize-y font-[inherit] leading-[1.6] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />
          <div className="mt-2 text-[13px] text-text-muted">
            {t('accountResearcher.postsEntered', { count: tweetsInput.split('\n').filter(l => l.trim()).length })}
          </div>
          <button
            onClick={handleAnalyze}
            disabled={!tweetsInput.trim() || status === 'analyzing'}
            className={cn(
              'w-full mt-4 py-4 border-none rounded-3xl text-base font-bold transition-all',
              !tweetsInput.trim() || status === 'analyzing'
                ? 'bg-surface-3 text-text-faint cursor-not-allowed'
                : 'bg-gradient-to-r from-danger to-warning text-white cursor-pointer hover:opacity-90'
            )}
          >
            {status === 'analyzing' ? t('common.analyzing') : t('accountResearcher.runAnalysis')}
          </button>
        </div>

        {/* Right: Results */}
        <div>
          {error && (
            <div className="p-4 bg-surface-2 border border-danger rounded-xl mb-4">
              <p className="text-danger m-0">{error}</p>
            </div>
          )}

          {result ? (
            <div className="space-y-4">
              {/* Common Patterns */}
              <div className="p-5 bg-surface-2 border border-danger rounded-xl">
                <h3 className="text-base mb-3 text-danger font-bold">{t('accountResearcher.commonPatterns')}</h3>
                <ul className="m-0 pl-5 text-text-primary space-y-2">
                  {result.commonPatterns.map((pattern, i) => (
                    <li key={i} className="leading-[1.5]">{pattern}</li>
                  ))}
                </ul>
              </div>

              {/* Tone Analysis */}
              <div className="p-5 bg-surface-2 border border-accent rounded-xl">
                <h3 className="text-base mb-3 text-accent font-bold">{t('accountResearcher.toneAnalysis')}</h3>
                <p className="text-[15px] leading-[1.6] m-0 text-text-primary">{result.toneAnalysis}</p>
              </div>

              {/* Topic Breakdown */}
              <div className="p-5 bg-surface-2 border border-warning rounded-xl">
                <h3 className="text-base mb-3 text-warning font-bold">{t('accountResearcher.topicBreakdown')}</h3>
                <div className="flex flex-col gap-2">
                  {Object.entries(result.topicBreakdown).map(([topic, pct]) => (
                    <div key={topic}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-text-primary">{topic}</span>
                        <span className="text-sm text-warning font-bold">{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-border rounded-full">
                        <div
                          className="h-full bg-gradient-to-r from-warning to-orange-500 rounded-full"
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Engagement Triggers */}
              <div className="p-5 bg-surface-2 border border-success rounded-xl">
                <h3 className="text-base mb-3 text-success font-bold">{t('accountResearcher.engagementTriggers')}</h3>
                <div className="flex flex-wrap gap-2">
                  {result.engagementTriggers.map((trigger, i) => (
                    <span key={i} className="px-3.5 py-2 bg-success/10 border border-success rounded-2xl text-[13px] text-success">
                      {trigger}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[400px] bg-surface-2 border-2 border-dashed border-border rounded-xl text-text-muted text-base text-center whitespace-pre-line">
              {t('accountResearcher.emptyState')}
            </div>
          )}

          {/* Saved Research */}
          {savedResearch.length > 0 && (
            <div className="mt-6">
              <h3 className="text-base mb-3 text-text-primary">{t('accountResearcher.pastAnalyses')}</h3>
              {savedResearch.slice(0, 5).map(item => (
                <div key={item.id} className="p-3 mb-2 bg-surface-2 border border-border rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-[13px] text-text-primary">
                      {item.accountName || t('accountResearcher.unnamed')} ({item.analyzedCount} posts)
                    </span>
                    <span className="text-xs text-text-muted">
                      {new Date(item.timestamp).toLocaleDateString(locale === 'ja' ? 'ja-JP' : 'en-US')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
